import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../config/firebaseConfig';
import useProjectFinanceText from './projectFinanceText';
import { ALL_PHASES, DATE_KEYS, phaseGroupOf } from './projectFinanceColumns';

const formatTimestamp = (timestamp) => {
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });
  }
  return timestamp || '';
};

// Every field becomes text, as on the old page (timestamps, lists, objects)
const formatValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object' && value.seconds) return formatTimestamp(value);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * Data of Proje Finans Yönetimi: the projects (all fields as text, as
 * before), the search (customer, short title, department), the column
 * filters (phase: exact; dates: contains) and the phase quick filter.
 */
const useProjectFinance = () => {
  const { label } = useProjectFinanceText();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [phaseGroup, setPhaseGroup] = useState(ALL_PHASES);
  const [selectedId, setSelectedId] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Projects'));
      const projectsData = querySnapshot.docs.map((docSnapshot) => {
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...Object.entries(data).reduce((acc, [key, value]) => {
            acc[key] = formatValue(value);
            return acc;
          }, {}),
        };
      });
      setProjects(projectsData);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error(label('toast.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [label]);

  useEffect(() => {
    fetchProjects();
    // Yalnızca açılışta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProjects = useMemo(() => {
    let filtered = projects.filter((project) => !project.isEmpty);

    // Search: customer, short title and department
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          (project.customer && project.customer.toString().toLowerCase().includes(lowercasedSearch)) ||
          (project.titleShort && project.titleShort.toString().toLowerCase().includes(lowercasedSearch)) ||
          (project.projectDepartment && project.projectDepartment.toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Column filters
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        if (key === 'projectPhase') {
          filtered = filtered.filter((project) => project[key] === filters[key]);
        } else if (DATE_KEYS.includes(key)) {
          filtered = filtered.filter((project) => project[key] && project[key].includes(filters[key]));
        } else {
          filtered = filtered.filter((project) => String(project[key] || '').toLowerCase().includes(filters[key].toLowerCase()));
        }
      }
    });

    if (phaseGroup !== ALL_PHASES) filtered = filtered.filter((project) => phaseGroupOf(project.projectPhase) === phaseGroup);

    return [...filtered].sort((a, b) => String(a.titleShort || '').localeCompare(String(b.titleShort || ''), 'tr'));
  }, [projects, filters, searchTerm, phaseGroup]);

  const stats = useMemo(() => {
    const counts = { total: projects.length, active: 0, passive: 0, starting: 0, other: 0 };
    projects.forEach((project) => { counts[phaseGroupOf(project.projectPhase)] += 1; });
    return counts;
  }, [projects]);

  const selectedProject = useMemo(() => projects.find((project) => project.id === selectedId) || null, [projects, selectedId]);

  const handleFilter = useCallback((column, value) => setFilters((current) => ({ ...current, [column]: value })), []);
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
    setPhaseGroup(ALL_PHASES);
  }, []);

  return {
    projects,
    isLoading,
    filteredProjects,
    stats,
    filters,
    handleFilter,
    clearFilters,
    searchTerm,
    setSearchTerm,
    phaseGroup,
    setPhaseGroup,
    selectedProject,
    setSelectedId,
  };
};

export default useProjectFinance;
